import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Home, Plus, DollarSign, Bed, Bath, MapPin, Trash2, AlertCircle, ArrowLeft, Edit, Search, ChevronLeft, ChevronRight, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrokerProperties = () => {
  const navigate = useNavigate();
  const { token, agent } = useAuth();
  const [properties, setProperties] = useState([]);
  const [agents, setAgents] = useState([]);
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddProperty, setShowAddProperty] = useState(false);
  const [showEditProperty, setShowEditProperty] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const [propertyForm, setPropertyForm] = useState({
    address: '',
    price: '',
    bedrooms: '',
    bathrooms: '',
    mls_number: '',
    property_type: '',
    description: '',
    status: 'available'
  });

  useEffect(() => {
    if (agent?.role === 'broker') {
      fetchData();
    }
  }, [agent]);

  const fetchData = async () => {
    try {
      const [propertiesRes, agentsRes, typesRes] = await Promise.all([
        axios.get(`${API}/broker/properties`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/broker/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/property-types`)
      ]);
      
      setProperties(propertiesRes.data);
      setAgents(agentsRes.data.filter(a => a.status === 'approved' || a.status === 'active'));
      setPropertyTypes(typesRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddProperty = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/broker/properties`, 
        {
          ...propertyForm,
          price: parseFloat(propertyForm.price),
          bedrooms: parseInt(propertyForm.bedrooms),
          bathrooms: parseFloat(propertyForm.bathrooms)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Property added successfully!');
      setShowAddProperty(false);
      setPropertyForm({
        address: '',
        price: '',
        bedrooms: '',
        bathrooms: '',
        mls_number: '',
        property_type: '',
        description: '',
        status: 'available'
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add property');
    }
  };

  const handleDeleteProperty = async (propertyId) => {
    if (!window.confirm('Are you sure you want to delete this property?')) return;
    
    try {
      await axios.delete(`${API}/broker/properties/${propertyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Property deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete property');
    }
  };

  const handleEditProperty = (e, property) => {
    e.stopPropagation();
    setSelectedProperty(property);
    setPropertyForm({
      address: property.address,
      price: property.price.toString(),
      bedrooms: property.bedrooms.toString(),
      bathrooms: property.bathrooms.toString(),
      mls_number: property.mls_number,
      property_type: property.property_type || '',
      description: property.description || '',
      status: property.status || 'available'
    });
    setShowEditProperty(true);
  };

  const handleUpdateProperty = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/broker/properties/${selectedProperty.id}`, 
        {
          ...propertyForm,
          price: parseFloat(propertyForm.price),
          bedrooms: parseInt(propertyForm.bedrooms),
          bathrooms: parseFloat(propertyForm.bathrooms)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Property updated successfully!');
      setShowEditProperty(false);
      setSelectedProperty(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update property');
    }
  };

  const toggleDescription = (propertyId) => {
    setExpandedDescriptions(prev => ({
      ...prev,
      [propertyId]: !prev[propertyId]
    }));
  };

  const openPropertyDetail = (property) => {
    navigate(`/broker/properties/${property.id}`);
  };

  if (agent?.role !== 'broker') {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto text-center py-12">
          <p className="text-lg text-stone-600">This page is only accessible to brokers</p>
        </div>
      </Layout>
    );
  }

  // Check if broker is pending approval
  if (agent?.status === 'pending') {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto">
          <Button
            onClick={() => navigate('/broker')}
            variant="outline"
            className="mb-6 border-[#1A4D2E] text-[#1A4D2E] hover:bg-[#1A4D2E] hover:text-white"
          >
            <ArrowLeft className="mr-2" size={18} />
            Back to Dashboard
          </Button>

          <Alert className="border-2 border-yellow-500 bg-yellow-50">
            <AlertCircle className="h-6 w-6 text-yellow-600" />
            <AlertTitle className="text-yellow-800 font-bold text-xl mb-2">Access Restricted</AlertTitle>
            <AlertDescription className="text-yellow-700">
              <p className="mb-3">
                You cannot access property management until your broker account is approved by an administrator.
              </p>
              <p className="font-semibold">
                Please wait for approval or contact the administrator for more information.
              </p>
            </AlertDescription>
          </Alert>

          <Card className="mt-6 p-12 text-center bg-stone-50">
            <Home className="mx-auto mb-4 text-stone-300" size={64} />
            <h2 className="text-lg sm:text-2xl font-bold text-stone-600 mb-2">Property Management</h2>
            <p className="text-stone-500">
              Once approved, you'll be able to add properties, assign agents, and manage open houses.
            </p>
          </Card>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A4D2E] mb-2" style={{fontFamily: '"Playfair Display", serif'}}>
              Property Listings
            </h1>
            <p className="text-stone-600">Manage your property inventory and schedule open houses</p>
          </div>

          <Dialog open={showAddProperty} onOpenChange={setShowAddProperty}>
            <DialogTrigger asChild>
              <Button className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143D24]">
                <Plus className="mr-2" size={20} />
                Add Property
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Property</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddProperty} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="address">Property Address *</Label>
                  <Input
                    id="address"
                    required
                    value={propertyForm.address}
                    onChange={(e) => setPropertyForm({...propertyForm, address: e.target.value})}
                    placeholder="123 Main St, City, State ZIP"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="price">Price *</Label>
                    <Input
                      id="price"
                      type="text"
                      required
                      value={propertyForm.price ? Number(propertyForm.price).toLocaleString('en-US') : ''}
                      onChange={(e) => {
                        // Remove commas and non-numeric characters except decimal
                        const rawValue = e.target.value.replace(/[^0-9.]/g, '');
                        setPropertyForm({...propertyForm, price: rawValue});
                      }}
                      placeholder="500,000"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mls_number">MLS Number *</Label>
                    <Input
                      id="mls_number"
                      required
                      value={propertyForm.mls_number}
                      onChange={(e) => setPropertyForm({...propertyForm, mls_number: e.target.value})}
                      placeholder="MLS123456"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="bedrooms">Bedrooms *</Label>
                    <Input
                      id="bedrooms"
                      type="number"
                      required
                      value={propertyForm.bedrooms}
                      onChange={(e) => setPropertyForm({...propertyForm, bedrooms: e.target.value})}
                      placeholder="3"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bathrooms">Bathrooms *</Label>
                    <Input
                      id="bathrooms"
                      type="number"
                      step="0.5"
                      required
                      value={propertyForm.bathrooms}
                      onChange={(e) => setPropertyForm({...propertyForm, bathrooms: e.target.value})}
                      placeholder="2.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="property_type">Property Type</Label>
                    <Select
                      value={propertyForm.property_type}
                      onValueChange={(value) => setPropertyForm({...propertyForm, property_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {propertyTypes.map(pt => (
                          <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={propertyForm.description}
                    onChange={(e) => setPropertyForm({...propertyForm, description: e.target.value})}
                    placeholder="Property details..."
                    rows={3}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAddProperty(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#1A4D2E] text-white hover:bg-[#143D24]">
                    Add Property
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>

          {/* Edit Property Dialog */}
          <Dialog open={showEditProperty} onOpenChange={setShowEditProperty}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Edit Property</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleUpdateProperty} className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="edit-address">Property Address *</Label>
                  <Input
                    id="edit-address"
                    required
                    value={propertyForm.address}
                    onChange={(e) => setPropertyForm({...propertyForm, address: e.target.value})}
                    placeholder="123 Main St, City, State ZIP"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="edit-price">Price *</Label>
                    <Input
                      id="edit-price"
                      type="text"
                      required
                      value={propertyForm.price ? Number(propertyForm.price).toLocaleString('en-US') : ''}
                      onChange={(e) => {
                        // Remove commas and non-numeric characters except decimal
                        const rawValue = e.target.value.replace(/[^0-9.]/g, '');
                        setPropertyForm({...propertyForm, price: rawValue});
                      }}
                      placeholder="500,000"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-mls_number">MLS Number *</Label>
                    <Input
                      id="edit-mls_number"
                      required
                      value={propertyForm.mls_number}
                      onChange={(e) => setPropertyForm({...propertyForm, mls_number: e.target.value})}
                      placeholder="MLS123456"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="edit-bedrooms">Bedrooms *</Label>
                    <Input
                      id="edit-bedrooms"
                      type="number"
                      required
                      value={propertyForm.bedrooms}
                      onChange={(e) => setPropertyForm({...propertyForm, bedrooms: e.target.value})}
                      placeholder="3"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-bathrooms">Bathrooms *</Label>
                    <Input
                      id="edit-bathrooms"
                      type="number"
                      step="0.5"
                      required
                      value={propertyForm.bathrooms}
                      onChange={(e) => setPropertyForm({...propertyForm, bathrooms: e.target.value})}
                      placeholder="2.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-property_type">Property Type</Label>
                    <Select
                      value={propertyForm.property_type}
                      onValueChange={(value) => setPropertyForm({...propertyForm, property_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {propertyTypes.map(pt => (
                          <SelectItem key={pt} value={pt}>{pt}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={propertyForm.description}
                    onChange={(e) => setPropertyForm({...propertyForm, description: e.target.value})}
                    placeholder="Property details..."
                    rows={4}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowEditProperty(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#1A4D2E] text-white hover:bg-[#143D24]">
                    Update Property
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search & Pagination Controls */}
        {!loading && properties.length > 0 && (
          <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="relative flex-1 w-full">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
              <Input
                placeholder="Search by address, MLS, type..."
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                className="pl-10 h-12 border-2 border-stone-200 rounded-lg"
                data-testid="property-search-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-500 whitespace-nowrap">Per page:</span>
              <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                <SelectTrigger className="w-20 h-10" data-testid="property-page-size">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="30">30</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {/* Properties List */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-stone-600">Loading properties...</p>
          </div>
        ) : properties.length === 0 ? (
          <Card className="bg-white p-12 rounded-2xl card-shadow text-center">
            <Home className="mx-auto mb-4 text-stone-300" size={64} />
            <h3 className="text-xl font-bold text-[#1A4D2E] mb-2">No Properties Yet</h3>
            <p className="text-stone-600 mb-6">Add your first property to start scheduling open houses</p>
            <Button onClick={() => setShowAddProperty(true)} className="bg-[#1A4D2E] text-white rounded-full hover:bg-[#143D24]">
              <Plus className="mr-2" size={20} />
              Add Your First Property
            </Button>
          </Card>
        ) : (() => {
          const q = searchQuery.toLowerCase();
          const filtered = properties.filter(p =>
            p.address?.toLowerCase().includes(q) ||
            p.mls_number?.toLowerCase().includes(q) ||
            p.property_type?.toLowerCase().includes(q) ||
            p.description?.toLowerCase().includes(q)
          );
          const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
          const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

          return (
            <>
              {filtered.length === 0 ? (
                <Card className="bg-white p-12 rounded-2xl card-shadow text-center">
                  <Search className="mx-auto mb-4 text-stone-300" size={48} />
                  <h3 className="text-lg font-semibold text-stone-700 mb-1">No matching properties</h3>
                  <p className="text-stone-500 text-sm">Try a different search term</p>
                </Card>
              ) : (
                <div className="space-y-3" data-testid="properties-list">
                  {paginated.map((property) => (
                    <Card
                      key={property.id}
                      className="bg-white rounded-xl card-shadow hover:card-shadow-hover transition-all cursor-pointer"
                      onClick={() => openPropertyDetail(property)}
                      data-testid={`property-row-${property.id}`}
                    >
                      <div className="flex items-start gap-3 sm:gap-4 p-3 sm:p-4">
                        <div className="p-2.5 sm:p-3 rounded-full bg-[#1A4D2E]/10 shrink-0 mt-0.5">
                          <Home className="text-[#1A4D2E]" size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-start gap-1 sm:gap-2 mb-1">
                            <h3 className="text-sm sm:text-base font-bold text-[#1A4D2E] break-words" style={{fontFamily: '"Playfair Display", serif'}}>
                              <MapPin className="inline mr-1 shrink-0" size={14} />
                              {property.address}
                            </h3>
                            {property.property_type && (
                              <span className="inline-block px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full shrink-0">
                                {property.property_type}
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:text-sm text-stone-600">
                            <span className="font-bold text-green-700">
                              <DollarSign className="inline" size={14} />
                              {new Intl.NumberFormat('en-US').format(property.price)}
                            </span>
                            <span><Bed className="inline mr-1" size={13} />{property.bedrooms} beds</span>
                            <span><Bath className="inline mr-1" size={13} />{property.bathrooms} baths</span>
                            <span className="text-xs text-stone-400">MLS: {property.mls_number}</span>
                          </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                          <Button size="sm" variant="ghost" onClick={(e) => handleEditProperty(e, property)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8 w-8 p-0">
                            <Edit size={16} />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteProperty(property.id); }} className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0">
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {filtered.length > 0 && (
                <div className="mt-6 flex items-center justify-between">
                  <p className="text-sm text-stone-500">
                    Showing {Math.min((currentPage - 1) * pageSize + 1, filtered.length)}-{Math.min(currentPage * pageSize, filtered.length)} of {filtered.length} properties
                  </p>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="properties-prev-page">
                      <ChevronLeft size={16} />
                    </Button>
                    <span className="text-sm font-medium text-stone-700 px-2">Page {currentPage} of {totalPages}</span>
                    <Button variant="outline" size="sm" disabled={currentPage >= totalPages} onClick={() => setCurrentPage(p => p + 1)} data-testid="properties-next-page">
                      <ChevronRight size={16} />
                    </Button>
                  </div>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </Layout>
  );
};

export default BrokerProperties;
